import type { FunctionComponent } from 'react';
import React from 'react';

import { BoardName } from 'app/src/components/BoardName';

import styles from './BoardHeader.less';
import { BoardTemplateBadge } from 'app/src/components/BoardTemplateBadge';
import { BoardHeaderButtonDivider } from './BoardHeaderButtonDivider';
import { useBoardHeaderQuery } from './BoardHeaderQuery.generated';
import { checkIsTemplate } from '@trello/boards';
import { BoardHeaderStarButton } from 'app/src/components/BoardHeaderStarButton';
import { memberId } from '@trello/session-cookie';

interface BoardHeaderProps {
  boardId: string;
}

/**
 * This component is an in-progress partial migration of the Board Header to a fully React/Graphql
 * based component. See this page for full scope and current progress:
 * https://hello.atlassian.net/wiki/spaces/TRELLO/pages/1602157428/Board+Header+re-write+scope
 *
 */
export const BoardHeader: FunctionComponent<BoardHeaderProps> = ({
  boardId,
}) => {
  const { data: boardHeaderData } = useBoardHeaderQuery({
    variables: { boardId },
    fetchPolicy: 'cache-only',
    waitOn: ['CurrentBoardFull'],
  });

  const isTemplate = boardHeaderData?.board?.prefs?.isTemplate ?? false;
  const showTemplateBadge = checkIsTemplate({
    isTemplate,
    permissionLevel: boardHeaderData?.board?.prefs?.permissionLevel,
    premiumFeatures: boardHeaderData?.board?.premiumFeatures,
  });

  return (
    <div className={styles['board-header']}>
      <BoardName boardId={boardId} />
      {/* BoardHeaderButtonDivider: Rendered here for testing. Can be removed after
      /*  other header components are re-written in React.
      */}
      <BoardHeaderButtonDivider />
      {showTemplateBadge && (
        <div className={styles.boardTemplateBadgeContainer}>
          <BoardTemplateBadge />
        </div>
      )}
      {memberId && (
        <BoardHeaderStarButton
          boardId={boardId}
          isTemplate={isTemplate}
          memberId={memberId}
        />
      )}
    </div>
  );
};
